import * as SwitchPrimitive from "@radix-ui/react-switch";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";
import { cn } from "../lib/cn";

/** 32×18 toggle. Pair with a visible label; pass `aria-label` when there is none. */
export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      "inline-flex h-[18px] w-8 shrink-0 cursor-pointer items-center rounded-pill p-0.5 transition-colors " +
        "data-[state=checked]:bg-accent data-[state=unchecked]:bg-border-strong disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb className="block size-3.5 rounded-pill bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-transform data-[state=checked]:translate-x-3.5" />
  </SwitchPrimitive.Root>
));
Switch.displayName = "Switch";

export interface SwitchRowProps extends ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label: string;
  description?: string;
}

/** Switch with label and description laid out as a row (the settings-toggle pattern). */
export function SwitchRow({ label, description, id, ...props }: SwitchRowProps) {
  const switchId = id ?? `switch-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="flex items-center gap-2.5">
      <Switch id={switchId} {...props} />
      <label htmlFor={switchId} className="flex cursor-pointer flex-col">
        <span className="text-base text-fg">{label}</span>
        {description ? <span className="text-sm text-fg-subtle">{description}</span> : null}
      </label>
    </div>
  );
}
